import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Flame, ClipboardList, BookOpen, BookOpenCheck, ListChecks, LayoutGrid, ChevronLeft, ChevronRight, CheckCircle2, Plus, Lock } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import Kipupiste from './Kipupiste';
import Peilaus from './Peilaus';
import StepCard from './StepCard';
import KyselyTask from './KyselyTask';
import SelfAssessmentTask from './SelfAssessmentTask';
import StaticTask from './StaticTask';
import TaskCard from './TaskCard';
// kuusi_kenttaa.v1 — registration point: the task card on /me (specs/workshop-modules/kuusi_kenttaa.md)
import KuusiKenttaaTask from '../../modules/kuusi_kenttaa_v1/KuusiKenttaaTask';

/**
 * PreWorkList — the Ennakkotehtävät tab.
 *
 * A workshop can set more than one pre-task, and they are different kinds of
 * thing: a Kipupiste question, a text to read, a link. A Kipupiste question
 * is a section, and under it **one card per pain point** the person has
 * written — each card owns its own Peilaus, the mirror of that pain point
 * against the knowledge base, so a pain point and its mirror are one thing
 * on the list, opened together. Opened, the two steps are minimised
 * sub-cards — the one still in progress is open, the rest are a header with
 * their status until tapped — so Peilaus is in view without scrolling past
 * the whole conversation. A person may write up to `max` (3) pain points per
 * question. The `peilaus` entry in `settings.preWork` configures
 * the mirror step; it is not a card of its own.
 */

const ICONS = {
  kipupiste: Flame,
  kysely: ListChecks,
  itsearviointi: ListChecks,
  peilaus: BookOpenCheck,
  reading: BookOpen,
  default: ClipboardList,
};

const CardButton = styled.button`
  all: unset;
  cursor: pointer;
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 1.1rem 1.35rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  &:hover, &:focus-visible { border-color: #3a4457; }
`;

const IconWrap = styled.div`
  flex: 0 0 auto;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$kind === 'kipupiste' ? 'rgba(239, 83, 80, 0.14)' : p.$kind === 'peilaus' ? 'rgba(242, 193, 78, 0.14)' : 'rgba(91, 157, 217, 0.14)')};
  color: ${(p) => (p.$kind === 'kipupiste' ? '#FF8A85' : p.$kind === 'peilaus' ? '#f2c14e' : '#8ec1f0')};
`;

const Locked = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #7b8794;
  font-size: 0.85rem;
`;

const Text = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Hint = styled.div`
  color: #98a2b3;
  font-size: 0.92rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled(ChevronRight)`
  flex: 0 0 auto;
  color: #7b8794;
`;

const Done = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #6fd3c8;
  font-size: 0.85rem;
  font-weight: 600;
`;

const BottomButton = styled.button`
  all: unset;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  color: #e8ecf2;
  border: 1px solid #2c3444;
  &:hover { border-color: #3a4457; }
`;

const Back = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #98a2b3;
  font-size: 0.92rem;
  &:hover { color: #e8ecf2; }
`;

const Intro = styled.p`
  margin: 0;
  color: #98a2b3;
  line-height: 1.5;
`;

const Pain = styled.div`
  border-left: 3px solid #EF5350;
  padding: 0.4rem 0 0.4rem 0.9rem;
  font-size: 1.05rem;
  font-weight: 600;
`;

const hintFor = (task) => task.question || task.body || task.linkText || task.url || '';

// The list's own column: the fragment it replaced inherited the page's gap;
// a plain div dropped it and the cards touched (29.9.2026).
const ListWrap = styled.div`display: flex; flex-direction: column; gap: 1.25rem;`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.2rem 0.35rem;
`;

const Steps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.9rem;
  color: #98a2b3;
  font-size: 0.85rem;
  b { color: #e8ecf2; font-weight: 600; }
  .done { color: #6fd3c8; }
  .open { color: #f2c14e; }
`;

// The facilitator grouped this pain point into a muuttuja (specs/prework-curation.md).
const Kind = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #98a2b3;
`;

const Merged = styled.div`
  color: #6fd3c8;
  font-size: 0.9rem;
  line-height: 1.4;
`;

// Another pain point is optional; the unanswered question is the ask, so
// this is a small text link at the section's bottom right, not a card.
const AddLink = styled.button`
  all: unset;
  cursor: pointer;
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: #98a2b3;
  padding: 0.1rem 0.35rem;
  &:hover, &:focus-visible { color: #e6e9ef; text-decoration: underline; }
`;

const AddCard = styled(CardButton)`
  border-style: dashed;
  color: #98a2b3;
  justify-content: center;
  padding: 0.85rem 1.35rem;
  &:hover, &:focus-visible { color: #e8ecf2; }
`;

/** Which Kipupiste question an interview answers; rows from before the index existed belong to the first. */
const belongsTo = (interview, questionIndex, firstQuestionIndex) =>
  interview.taskIndex === questionIndex || (interview.taskIndex == null && questionIndex === firstQuestionIndex);

export default function PreWorkList({ instanceId, tasks, status = null, locked = false, kuusiKenttaa = false, selfAssessment = true, onTodoChange = null }) {
  const { t } = useTranslation();
  // What is open lives in the URL so a refresh keeps it: ?tehtava=N a static
  // task (or, with &uusi=1, the form for a new pain point under question N);
  // ?kipupiste=<id> one pain point with its mirror; ?tehtava=kuusi-kenttaa the six fields.
  const [searchParams, setSearchParams] = useSearchParams();
  const taskParam = searchParams.has('tehtava') && tasks[Number(searchParams.get('tehtava'))] ? Number(searchParams.get('tehtava')) : null;
  const adding = taskParam !== null && searchParams.get('uusi') === '1';
  const openInterviewId = searchParams.get('kipupiste');
  const itseOpen = selfAssessment && searchParams.get('tehtava') === 'itsearviointi';
  const kkOpen = kuusiKenttaa && searchParams.get('tehtava') === 'kuusi-kenttaa';
  const go = (next) => setSearchParams(next);
  const openStatic = (i) => go({ tehtava: String(i) });
  const openNew = (i) => go({ tehtava: String(i), uusi: '1' });
  const openPain = (id) => go({ kipupiste: id });
  const close = () => go({});

  const peilausTask = tasks.find((task) => task.kind === 'peilaus') || null;
  const firstQuestion = tasks.findIndex((task) => task.kind === 'kipupiste');
  const prevQuestion = (i) => { for (let j = i - 1; j >= 0; j -= 1) if (tasks[j].kind === 'kipupiste') return j; return -1; };

  // The person's pain points and mirrors; asked on arrival and again each
  // time a card opens or closes, so the cards say what was just done.
  const [interviews, setInterviews] = useState(null);
  const [mirrors, setMirrors] = useState(null);
  const isOpen = openInterviewId !== null || taskParam !== null || itseOpen || kkOpen;
  useEffect(() => {
    let cancelled = false;
    const q = `?instanceId=${encodeURIComponent(instanceId)}`;
    fetch(API_ENDPOINTS.ME_KYSY() + q, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { interviews: [] }))
      .then((d) => { if (!cancelled) setInterviews(d.interviews || []); })
      .catch(() => { if (!cancelled) setInterviews([]); });
    fetch(API_ENDPOINTS.ME_PEILAUS() + q, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { mirrors: [] }))
      .then((d) => { if (!cancelled) setMirrors(d.mirrors || []); })
      .catch(() => { if (!cancelled) setMirrors([]); });
    return () => { cancelled = true; };
  }, [instanceId, isOpen]);

  // The Kysely's state, asked on arrival and after each open, like the interviews.
  const [kysely, setKysely] = useState(null);
  const hasKysely = tasks.some((task) => task.kind === 'kysely');
  useEffect(() => {
    if (!hasKysely) return undefined;
    let cancelled = false;
    fetch(`${API_ENDPOINTS.ME_KYSELY()}?instanceId=${encodeURIComponent(instanceId)}`, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setKysely(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId, isOpen, hasKysely]);

  // The self-assessment (#693): first card of every workshop unless the
  // workshop switched it off (settings.selfAssessment = false).
  // Before the workshop it is the pre wave; once the workshop is past, the post wave.
  const wave = 'pre';
  const [itse, setItse] = useState(null);
  useEffect(() => {
    if (!selfAssessment) return undefined;
    let cancelled = false;
    fetch(`${API_ENDPOINTS.ME_SELF_ASSESSMENT()}?instanceId=${encodeURIComponent(instanceId)}&wave=${wave}`, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setItse(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId, isOpen, wave, selfAssessment]);

  const all = interviews || [];
  const mirrorOf = (interview) => (mirrors || []).find((m) => m.nodeId === interview.nodeId) || null;
  const anyOpen = all.some((i) => i.status === 'open');

  // The tab's dot: a question with no pain point yet or one still open, or a
  // finished pain point whose mirror is not answered. Told to the page after
  // each load; the backend applies the same rule for the first paint.
  useEffect(() => {
    if (!onTodoChange || interviews === null) return;
    const questions = tasks.map((task, i) => [task, i]).filter(([task]) => task.kind === 'kipupiste');
    const unlocked = ([, i]) => { const p = prevQuestion(i); return p < 0 || all.some((iv) => belongsTo(iv, p, firstQuestion) && iv.status === 'done'); };
    const unanswered = questions.filter(unlocked).some(([, i]) => !all.some((iv) => belongsTo(iv, i, firstQuestion)));
    const unmirrored = !!peilausTask && all.some((iv) => iv.status === 'done' && mirrorOf(iv)?.status !== 'done');
    const kyselyTodo = hasKysely && kysely !== null && !kysely.submitted;
    const itseTodo = itse !== null && !itse.submitted;
    // Locked pre-work has nothing to do: the dot gives way to the lock.
    onTodoChange(!locked && (unanswered || anyOpen || unmirrored || kyselyTodo || itseTodo));
  }, [onTodoChange, interviews, mirrors, tasks, kysely, itse, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  // One pain point: its conversation and its mirror as two sub-cards. The
  // step in progress starts open; a finished or locked one stays minimised
  // until tapped, so the mirror is in view without scrolling.
  const interview = openInterviewId !== null ? all.find((i) => i.id === openInterviewId) || null : null;
  const mirror = interview ? mirrorOf(interview) : null;
  const painDone = interview?.status === 'done';
  const mirrorDone = mirror?.status === 'done';
  const loaded = interviews !== null && mirrors !== null;
  const defaultOpen = useMemo(() => {
    if (!interview || !loaded) return null;
    if (!painDone) return 'kipupiste';
    if (peilausTask && !mirrorDone) return 'peilaus';
    return null;
  }, [openInterviewId, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [openStep, setOpenStep] = useState(null);
  useEffect(() => { setOpenStep(defaultOpen); }, [defaultOpen]);
  const toggle = (step) => setOpenStep((prev) => (prev === step ? null : step));

  if (openInterviewId !== null) {
    const question = interview && tasks[interview.taskIndex ?? firstQuestion]?.kind === 'kipupiste'
      ? tasks[interview.taskIndex ?? firstQuestion] : tasks[firstQuestion] || {};
    return (
      <>
        <Back onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBack')}</Back>
        {interview && <Kind>{question.title || t('me.kipupiste.label')}</Kind>}
        {interview && <Pain>{interview.painPoint}</Pain>}
        <StepCard kind="kipupiste" icon={ICONS.kipupiste} title={question.title || t('me.kipupiste.label')}
                  status={painDone ? t('me.preWorkDone') : t('me.kipupiste.inProgress')} tone={painDone ? 'done' : 'open'}
                  open={openStep === 'kipupiste'} onToggle={() => toggle('kipupiste')}>
          <Kipupiste instanceId={instanceId} task={question} interviewId={openInterviewId} readOnly={locked}
                     onChange={(next) => setInterviews((prev) => [...(prev || []).filter((i) => i.id !== next.id), next])} />
        </StepCard>
        {peilausTask && (
          <StepCard kind="peilaus" icon={ICONS.peilaus} title={peilausTask.title || t('me.peilaus.label')}
                    status={!painDone ? t('me.preWorkLocked') : mirrorDone ? t('me.preWorkDone') : mirror ? t('me.kipupiste.inProgress') : t('me.peilaus.start')}
                    tone={!painDone ? 'locked' : mirrorDone ? 'done' : mirror ? 'open' : 'idle'}
                    open={openStep === 'peilaus'} onToggle={() => toggle('peilaus')}>
            <Peilaus instanceId={instanceId} task={peilausTask} interviewId={openInterviewId} readOnly={locked}
                     onChange={(p) => setMirrors((prev) => [...(prev || []).filter((m) => m.id !== p.id), p])} />
          </StepCard>
        )}
        <BottomButton onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBackToList')}</BottomButton>
      </>
    );
  }

  if (itseOpen) {
    return (
      <>
        <Back onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBack')}</Back>
        <SelfAssessmentTask instanceId={instanceId} wave={wave} onChange={(d) => setItse(d)} />
        <BottomButton onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBackToList')}</BottomButton>
      </>
    );
  }

  if (kkOpen) {
    return (
      <>
        <Back onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBack')}</Back>
        <Kind>{t('kuusiKenttaa.title')}</Kind>
        <KuusiKenttaaTask instanceId={instanceId} locked={locked} />
        <BottomButton onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBackToList')}</BottomButton>
      </>
    );
  }

  // A static task, or the form for a new pain point under a question.
  if (taskParam !== null) {
    const task = tasks[taskParam];
    return (
      <>
        <Back onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBack')}</Back>
        {task.kind === 'kipupiste' && adding
          ? <Kipupiste instanceId={instanceId} task={task} taskIndex={taskParam} initialAdding
                       onChange={(next) => { setInterviews((prev) => [...(prev || []).filter((i) => i.id !== next.id), next]); openPain(next.id); }} />
          : task.kind === 'kysely'
            ? <>{task.title && <Kind>{task.title}</Kind>}<KyselyTask instanceId={instanceId} onChange={(d) => setKysely(d)} /></>
            : <StaticTask task={task} />}
        <BottomButton onClick={close}><ChevronLeft size={16} /> {t('me.preWorkBackToList')}</BottomButton>
      </>
    );
  }

  const Flame = ICONS.kipupiste;
  return (
    <ListWrap>
      <Intro>{locked ? t('me.preWorkClosed') : t('me.preWorkIntro')}</Intro>
      {selfAssessment && (
        <TaskCard icon={ICONS.itsearviointi} title={t('me.selfAssessment.label')}
          hint={itse?.submitted ? t('me.selfAssessment.done') : t('me.selfAssessment.hint')}
          state={itse?.submitted ? 'done' : locked ? 'locked' : 'open'} doneLabel={t('me.preWorkDone')} lockedLabel={t('me.preWorkClosedShort')}
          onClick={() => go({ tehtava: 'itsearviointi' })} />
      )}
      {kuusiKenttaa && (
        <TaskCard icon={LayoutGrid} title={t('kuusiKenttaa.title')} hint={t('kuusiKenttaa.hint')}
          state={locked ? 'locked' : 'open'} doneLabel={t('me.preWorkDone')} lockedLabel={t('me.preWorkClosedShort')}
          onClick={() => go({ tehtava: 'kuusi-kenttaa' })} />
      )}
      {tasks.map((task, i) => {
        // The mirror is a step inside each pain point, never a card of its own.
        if (task.kind === 'peilaus') return null;
        if (task.kind !== 'kipupiste') {
          const Icon = ICONS[task.kind] || ICONS.default;
          const isKysely = task.kind === 'kysely';
          const kyselyDone = isKysely && kysely?.submitted;
          return (
            <CardButton key={i} onClick={() => openStatic(i)}>
              <IconWrap $kind={task.kind}><Icon size={20} /></IconWrap>
              <Text>
                <strong>{task.title || (isKysely ? t('me.kysely.label') : t('me.preWorkUntitled'))}</strong>
                {isKysely ? <Hint>{kyselyDone ? t('me.kysely.done') : (task.hint || t('me.kysely.hint'))}</Hint> : (hintFor(task) && <Hint>{hintFor(task)}</Hint>)}
              </Text>
              {kyselyDone ? <Done><CheckCircle2 size={18} /> {t('me.preWorkDone')}</Done> : <Chevron size={18} />}
            </CardButton>
          );
        }
        const mine = all.filter((iv) => belongsTo(iv, i, firstQuestion));
        const max = Number(task.max) > 0 ? Number(task.max) : 3;
        const canAdd = !locked && interviews !== null && !anyOpen && mine.length < max;
        // A later question opens once the one before it has a finished pain point.
        const prev = prevQuestion(i);
        const gated = prev >= 0 && !all.some((iv) => belongsTo(iv, prev, firstQuestion) && iv.status === 'done');
        if (gated) {
          return (
            <Section key={i} style={{ opacity: 0.6 }}>
              <SectionHead>
                <IconWrap $kind="kipupiste"><Lock size={20} /></IconWrap>
                <Text>
                  <strong>{task.title || t('me.preWorkUntitled')}</strong>
                  <Hint>{t('me.preWorkLockedAfter', { title: tasks[prev].title || t('me.kipupiste.label') })}</Hint>
                </Text>
              </SectionHead>
            </Section>
          );
        }
        return (
          <Section key={i}>
            <SectionHead>
              <IconWrap $kind="kipupiste"><Flame size={20} /></IconWrap>
              <Text>
                <strong>{task.title || t('me.preWorkUntitled')}</strong>
                {task.question && <Hint>{task.question}</Hint>}
              </Text>
            </SectionHead>
            {mine.map((iv) => {
              const mirror = mirrorOf(iv);
              const painDone = iv.status === 'done';
              const mirrorDone = mirror?.status === 'done';
              const allDone = painDone && (!peilausTask || mirrorDone);
              return (
                <CardButton key={iv.id} onClick={() => openPain(iv.id)}>
                  <Text>
                    <Kind>{task.title || t('me.kipupiste.label')}</Kind>
                    <strong>{iv.painPoint}</strong>
                    {iv.mergedInto && (
                      <Merged>
                        {t('me.kipupiste.mergedInto', { title: iv.mergedInto.title, scenario: iv.mergedInto.scenario || '' })}
                        {iv.mergedInto.others > 0 && ` ${t('me.kipupiste.mergedWith', { count: iv.mergedInto.others })}`}
                      </Merged>
                    )}
                    <Steps>
                      <span>{t('me.kipupiste.label')}: <b className={painDone ? 'done' : 'open'}>{painDone ? t('me.preWorkDone') : t('me.kipupiste.inProgress')}</b></span>
                      {peilausTask && (
                        <span>{peilausTask.title || t('me.peilaus.label')}: <b className={mirrorDone ? 'done' : mirror ? 'open' : ''}>
                          {!painDone ? t('me.preWorkLocked') : mirrorDone ? t('me.preWorkDone') : mirror ? t('me.kipupiste.inProgress') : t('me.peilaus.start')}
                        </b></span>
                      )}
                    </Steps>
                  </Text>
                  {allDone ? <Done><CheckCircle2 size={18} /> {t('me.preWorkDone')}</Done>
                    : iv.status === 'open' ? <Locked>{t('me.kipupiste.inProgress')}</Locked>
                      : <Chevron size={18} />}
                </CardButton>
              );
            })}
            {canAdd && !mine.length && (
              <AddCard onClick={() => openNew(i)}>
                <Plus size={16} /> {t('me.kipupiste.addFirst')}
              </AddCard>
            )}
            {canAdd && mine.length > 0 && (
              <AddLink onClick={() => openNew(i)}>
                <Plus size={14} /> {t('me.kipupiste.addAnother', { count: mine.length, max })}
              </AddLink>
            )}
            {mine.length >= max && <Hint style={{ whiteSpace: 'normal', padding: '0 0.35rem' }}>{t('me.kipupiste.limit', { max })}</Hint>}
          </Section>
        );
      })}
    </ListWrap>
  );
}
